/**
 * What every child screen shares for one sitting: the event queue, where she
 * is (for a request's context and for „Baigti"'s `item.left`), and the one
 * Enter handler per screen.
 */
import { createContext, useContext, useEffect, useRef } from "react";

import { ulid } from "../lib/ulid";
import type { EventQueue } from "./queue";

export interface Where {
  item?: string;
  test?: string;
  /** The surface state she would leave from: KLAUSIAMA, DURYS, BEATS, RUNG… */
  state: string;
  rung: number;
  where: "item" | "explanation" | "idle";
  /** An open item that „Baigti" or „ne aš" leaves (`item.left`); closed items are not left. */
  open: boolean;
  /** Called on „Baigti" before the sitting ends: an open explanation closes itself. */
  onLeave?: () => void;
}

export interface ChildCtx {
  name: string;
  session: string;
  shared: boolean;
  emit(type: string, body: Record<string, unknown>): string;
  queue: EventQueue;
  setWhere(w: Where | null): void;
  /** A 409 `{need: picker}` from anything: the sitting is gone (idle, restart). */
  lost(e: unknown): boolean;
}

export const Ctx = createContext<ChildCtx | null>(null);

export function useChild(): ChildCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error("no sitting");
  return c;
}

export function makeEmit(queue: EventQueue) {
  return (type: string, body: Record<string, unknown>): string => {
    const id = ulid();
    queue.push({ id, type, body, client_at: Date.now() });
    return id;
  };
}

/**
 * One key for the screen's primary act (Enter, or others), on the document.
 * A focused button keeps its own Enter and Space (her Tab stop on the request
 * button raises a request, never a „Toliau"). Held keys never act twice.
 */
export function useKeys(map: Record<string, (e: KeyboardEvent) => void>, enabled = true): void {
  const ref = useRef(map);
  ref.current = map;
  useEffect(() => {
    if (!enabled) return;
    const h = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.repeat) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "BUTTON" || t.tagName === "A") && (e.key === "Enter" || e.key === " ")) return;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA") && e.key !== "Enter" && e.key !== "Escape") return;
      const byCode = ref.current[`code:${e.code}`];
      const fn = ref.current[e.key] ?? byCode;
      if (!fn) return;
      e.preventDefault();
      fn(e);
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [enabled]);
}
